import { UnitName as UnitNameEnum } from "@sinch/core/entities/serverEnums";
import { RatelistType, UnitName, UnitType } from "@sinch/core/entities/serverEnums.types";
import { useFormat } from "@sinch/core/hooks/format";
import { useSettings } from "@sinch/core/providers/AppSettings";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { pluck, zipObj } from "ramda";
import { isNotNil } from "ramda-adjunct";
import { useTranslation } from "react-i18next";
import { useEntityProps } from "../utilsHooks";
import { RatelistUnits } from "@sinch/components/application/Rate/queries.graphql";
import { RatelistUnitsQuery, RatelistUnitsQueryVariables } from "@sinch/components/application/Rate/queries.types";

/**
 * Single source of truth for how a rate unit type of a ratelist looks in the app:
 * label, description, unit symbol and formatter.
 *
 * Use these getters wherever a rate unit type of a ratelist is shown.
 * Never hardcode its label, description, unit symbol and formatter in a component.
 * The values come from the server, so there is no story for this hook.
 */
export const useUnitType = (ratelistType: RatelistType) => {
  const { data } = useQueryHandler<RatelistUnitsQuery, RatelistUnitsQueryVariables>(RatelistUnits, {
    ratelistType: ratelistType,
  });

  const { getRateName, getUnitFormatter, getUnitSymbol } = useUnitName();

  const ratelistUnits = data?.ratelistUnits ?? [];

  const unitTypes: Record<
    UnitType,
    {
      label: string;
      description: string;
      unitFormatter: ((value: number) => string) | null;
      unitSymbol: string | null;
      unitName: UnitName | null;
      rateName: string | null;
    }
  > = zipObj(
    pluck("unitType", ratelistUnits),
    ratelistUnits.map((ratelistUnit) => ({
      label: ratelistUnit.label,
      description: ratelistUnit.description,
      unitName: ratelistUnit.unitName,
      ...(isNotNil(ratelistUnit.unitName)
        ? {
            unitFormatter: getUnitFormatter(ratelistUnit.unitName),
            unitSymbol: getUnitSymbol(ratelistUnit.unitName),
            rateName: getRateName(ratelistUnit.unitName),
          }
        : {
            unitFormatter: null,
            unitSymbol: null,
            rateName: null,
          }),
    }))
  );

  const getProp = useEntityProps(unitTypes);

  const getUnitTypeLabel = getProp<string>("label", "");
  const getUnitTypeDescription = getProp<string>("description", "");
  const unitSymbolFormatter = getProp<((val: number) => string) | null>("unitFormatter", null);
  const unitSymbolGetter = getProp<string | null>("unitSymbol", null);
  const getUnitName = getProp<UnitName | null>("unitName", null);
  const getRateNameGetter = getProp<string | null>("rateName", null);

  const getOptionsList = () =>
    Object.entries(unitTypes).map(([key, value]) => ({
      value: key,
      label: value.label,
      description: value.description,
    }));

  return {
    getUnitTypeLabel,
    getUnitTypeDescription,
    getUnitFormatter: unitSymbolFormatter,
    getUnitName,
    getOptionsList,
    getUnitSymbol: unitSymbolGetter,
    getRateName: getRateNameGetter,
  };
};

/**
 * Single source of truth for how a unit name looks in the app: unit symbol, value formatter and rate name.
 *
 * Use these getters wherever a unit name is shown.
 * Never hardcode its unit symbol, value formatter and rate name in a component, change them here.
 * Every value is shown in the "Entity Identity/UnitName" story.
 */
export const useUnitName = () => {
  const { t } = useTranslation();
  const { locale } = useSettings();
  const { num } = useFormat();

  const unitNamesFormatters: Record<
    UnitName,
    {
      unitFormatter: ((value: number) => string) | null;
      unitSymbol: string | null;
      rateName: string | null;
    }
  > = {
    [UnitNameEnum.Hour]: {
      unitFormatter: formatUnit(locale, "hour"),
      unitSymbol: getUnitSymbol(locale, "hour"),
      rateName: t("RateUnit.hourRate"),
    },
    [UnitNameEnum.Kilometer]: {
      unitFormatter: formatUnit(locale, "kilometer"),
      unitSymbol: getUnitSymbol(locale, "kilometer"),
      rateName: t("RateUnit.distanceRate"),
    },
    [UnitNameEnum.Mile]: {
      unitFormatter: formatUnit(locale, "mile"),
      unitSymbol: getUnitSymbol(locale, "mile"),
      rateName: t("RateUnit.distanceRate"),
    },
    [UnitNameEnum.Pieces]: {
      unitFormatter: (val: number) => `${String(val)}${t("pieceAbbr")}`,
      unitSymbol: t("pieceAbbr"),
      rateName: t("RateUnit.pieceRate"),
    },
    [UnitNameEnum.Currency]: {
      unitFormatter: num.currency,
      unitSymbol: null,
      rateName: null,
    },
  };

  const getProp = useEntityProps(unitNamesFormatters);

  const unitSymbolFormatter = getProp<((val: number) => string) | null>("unitFormatter", null);
  const unitSymbolGetter = getProp<string | null>("unitSymbol", null);
  const getRateName = getProp<string | null>("rateName", null);

  return {
    getUnitFormatter: unitSymbolFormatter,
    getUnitSymbol: unitSymbolGetter,
    getRateName,
  };
};

type IntlUnitValues =
  | "acre"
  | "bit"
  | "byte"
  | "celsius"
  | "centimeter"
  | "day"
  | "degree"
  | "fahrenheit"
  | "fluid-ounce"
  | "foot"
  | "gallon"
  | "gigabit"
  | "gigabyte"
  | "gram"
  | "hectare"
  | "hour"
  | "inch"
  | "kilobit"
  | "kilobyte"
  | "kilogram"
  | "kilometer"
  | "liter"
  | "megabit"
  | "megabyte"
  | "meter"
  | "microsecond"
  | "mile"
  | "mile-scandinavian"
  | "milliliter"
  | "millimeter"
  | "millisecond"
  | "minute"
  | "month"
  | "nanosecond"
  | "ounce"
  | "percent"
  | "petabyte"
  | "pound"
  | "second"
  | "stone"
  | "terabit"
  | "terabyte"
  | "week"
  | "yard"
  | "year"
  | string;

function formatUnit(
  locale: string | undefined = undefined,
  unit: IntlUnitValues,
  display: "narrow" | "short" | "long" = "narrow",
  maximumFractionDigits: number = 2
) {
  if (!Intl.supportedValuesOf("unit").includes(unit)) {
    return (val: number) => String(val);
  }

  const nf = new Intl.NumberFormat(locale, {
    style: "unit",
    unit: unit,
    unitDisplay: display, // 'narrow' | 'short' | 'long'
    maximumFractionDigits,
  });
  return nf.format;
}

function getUnitSymbol(
  locale: string | undefined = undefined,
  unit: IntlUnitValues,
  display: "narrow" | "short" | "long" = "narrow"
) {
  if (!Intl.supportedValuesOf("unit").includes(unit)) {
    return unit;
  }

  const nf = new Intl.NumberFormat(locale, {
    style: "unit",
    unit: unit,
    unitDisplay: display, // 'narrow' | 'short' | 'long'
  });
  const parts = nf.formatToParts(1);
  // In some locales order differs, so find the unit part
  const unitPart = parts.find((p) => p.type === "unit");

  return unitPart?.value ?? "";
}
